import { isUndefined } from 'lodash-es';
import { useIntl } from 'react-intl';

import { ZoomToSelectionIcon } from '@dynatrace/strato-icons';

import { TooltipHeader } from './TooltipHeader.js';
import { getCustomContextMenuItems } from '../../../core/components/interactions-overlay/utils/get-custom-context-menu-items.js';
import { useOverlay } from '../../../core/components/overlay-tooltip/contexts/OverlayContext.js';
import { OverlayTooltip } from '../../../core/components/overlay-tooltip/OverlayTooltip.js';
import { SelectionTooltipWithAsyncActions } from '../../../core/components/selection-area/SelectionTooltipWithAsyncActions.js';
import { getSelectionTooltipPosition } from '../../../core/components/selection-area/tooltip/get-selection-tooltip-position.js';
import { usePointerAndScrollResponsiveChartBounds } from '../../../core/hooks/usePointerAndScrollResponsiveChartBounds.js';
import { useSelectConfig } from '../../hooks/useSelectConfig.js';
import { useXYChartData } from '../../hooks/useXYChartData.js';
import { XYCHART_MESSAGES } from '../../messages/messages.js';
import { useSelectState } from '../../store/selectors.js';
import type {
  XYChartSelectedDomains,
  XYChartSelectedSeries,
} from '../../types/xy-chart.js';
import { getDatapointsMessage } from '../../utils/get-datapoints-message.js';
import { useZoomToSelection } from '../overlay/hooks/useZoomToSelection.js';
import type { XYChartSelectedDatapointsPerSeries } from '../overlay/utils/get-selected-datapoints.js';

export const XYChartSelectionAreaTooltip = ({
  selectedDatapoints,
  totalSeries: seriesCount,
  totalCategories: categoriesCount,
  selectedDatapointsPerSeries,
}: {
  selectedDatapoints: number;
  totalSeries: number;
  totalCategories: number;
  selectedDatapointsPerSeries: XYChartSelectedDatapointsPerSeries[];
}) => {
  const selectState = useSelectState();
  const { axesMetadata, seriesWithDatapoints } = useXYChartData();
  const selectConfig = useSelectConfig();

  if (isUndefined(selectState)) {
    return null;
  }

  const selectedDatapointsWithData = selectedDatapointsPerSeries.filter(
    (data) => data.datapoints.length,
  );

  const selectedSeries = selectedDatapointsWithData.map((data) => {
    return {
      [data.seriesName]:
        data.datapoints as XYChartSelectedSeries[number][string],
    };
  });

  const selectionDomain: XYChartSelectedDomains =
    selectedDatapointsWithData.map((data) => {
      return {
        [data.seriesName]: data.domain as [Date, Date] | [number, number],
      };
    });

  const datapointsMessage = getDatapointsMessage(
    selectedDatapoints,
    seriesCount,
    seriesWithDatapoints.length,
    categoriesCount,
  );

  return (
    <DynamicSelectionAreaTooltip
      selectedDatapoints={selectedDatapoints}
      selectState={selectState}
      categoriesCount={categoriesCount}
      seriesCount={seriesCount}
      axesMetadata={axesMetadata}
      selectedSeries={selectedSeries}
      selectionDomain={selectionDomain}
      selectConfig={selectConfig}
      datapointsMessage={datapointsMessage}
    />
  );
};

const DynamicSelectionAreaTooltip = ({
  selectedDatapoints,
  selectState,
  categoriesCount,
  seriesCount,
  axesMetadata,
  selectedSeries,
  selectionDomain,
  selectConfig,
  datapointsMessage,
}: {
  selectedDatapoints: number;
  selectState: ReturnType<typeof useSelectState>;
  categoriesCount: number;
  seriesCount: number;
  axesMetadata: ReturnType<typeof useXYChartData>['axesMetadata'];
  selectedSeries: XYChartSelectedSeries;
  selectionDomain: XYChartSelectedDomains;
  selectConfig: ReturnType<typeof useSelectConfig>;
  datapointsMessage: ReturnType<typeof getDatapointsMessage>;
}) => {
  const intl = useIntl();
  const zoomToSelection = useZoomToSelection();
  const { overlayRef: chartRef } = useOverlay();
  const chartBounds = usePointerAndScrollResponsiveChartBounds(
    chartRef,
    true,
  )();

  if (isUndefined(selectState) || !chartBounds) {
    return null;
  }

  const tooltipPosition = getSelectionTooltipPosition(selectState, chartBounds);

  return (
    <SelectionTooltipWithAsyncActions
      actions={() => {
        const actions = selectConfig?.actions;
        return (
          actions &&
          (async () => actions(selectedSeries, selectionDomain))().then(
            getCustomContextMenuItems,
          )
        );
      }}
      selectFinished={selectState.finished}
      header={
        <TooltipHeader
          subheader={intl.formatMessage(datapointsMessage, {
            count: selectedDatapoints,
            seriesCount: seriesCount + categoriesCount,
          })}
        />
      }
      position={tooltipPosition}
      zoomEnabled={axesMetadata.isZoomEnabled}
      range={selectState.selectionPercentage || [0, 0]}
    >
      {axesMetadata.isZoomEnabled ? (
        <OverlayTooltip.Action
          label={intl.formatMessage(XYCHART_MESSAGES.zoomToSelection)}
          icon={<ZoomToSelectionIcon />}
          onSelect={zoomToSelection}
          trailingIcon="⏎"
        />
      ) : null}
    </SelectionTooltipWithAsyncActions>
  );
};
